import { ButtonHierarchy, ButtonSize, MainButton } from "@beydesign/storybook"
import { Box } from "@mui/material"
import { Warning } from "@phosphor-icons/react"
import React, { useState } from "react"
import { useAppContext, useAppDispatchContext } from "../../app/Context"
import { useBranding } from "../../hooks"
import { getMedia, isPermissionDeniedError } from "../../lib/getMedia"
import { Agent } from "../../types/Agent"
import { StateAction } from "../../types/State"
import { SnackbarType } from "../../types/UI"
import { VideoAndMicAccessModal } from "../Modal/VideoAndMicAccess"
import { SnackbarWithText } from "../snackbar"

const tosLink =
  "https://beyond-presence.notion.site/Terms-of-Use-86de0b8f3b60441bb829efa77a269e31"
const privacyLink =
  "https://beyond-presence.notion.site/Privacy-Policy-86de0b8f3b60441bb829efa77a269e31"

interface BottomProps {
  agent: Agent
  agentExists: boolean
  handleGrantPermissions: (granted: boolean) => void
}

const linkStyle = {
  color: "var(--color-text-text-clicable)",
  cursor: "pointer",
  textDecoration: "none",
}

export const Bottom: React.FC<BottomProps> = ({
  agent,
  handleGrantPermissions,
  agentExists,
}) => {
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false)
  const [errorOpen, setErrorOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const context = useAppContext()
  const dispatch = useAppDispatchContext()
  const branding = useBranding()

  const reportMediaError = (error: Error) => {
    setErrorMessage(
      isPermissionDeniedError(error)
        ? "Camera and microphone access was denied. Please enable permissions in your browser settings and try again."
        : "Unable to access camera or microphone. Please check your device settings and try again.",
    )
    setErrorOpen(true)
  }

  const requestMediaAccess = async () => {
    setIsLoading(true)
    setPermissionsModalOpen(true)
    try {
      const stream = await getMedia(reportMediaError, context)
      if (stream != null) {
        dispatch({ type: StateAction.setStream, payload: stream })
        setPermissionsModalOpen(false)
        handleGrantPermissions(true)
        return
      }
      setPermissionsModalOpen(false)
    } catch (error) {
      reportMediaError(error as Error)
      setPermissionsModalOpen(false)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAllowMicrophoneAndCamera = () => {
    requestMediaAccess()
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          display: permissionsModalOpen ? "none" : "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: "var(--spacing-tokens-size-in-px-spacing-spacing-3xl)",
          paddingX: "var(--spacing-tokens-size-in-px-spacing-spacing-3xl)",
          paddingBottom: "var(--spacing-tokens-size-in-px-spacing-spacing-lg)",
          paddingTop: "var(--spacing-tokens-size-in-px-spacing-spacing-9xl)",
          background:
            "linear-gradient(180deg, rgba(12, 11, 37, 0) 0%, rgba(12, 11, 37, 0.55) 60%, rgba(12, 11, 37, 0.75) 100%)",
        }}
      >
        {!agentExists && (
          <Warning
            size={48}
            weight="light"
            color="var(--color-text-text-white)"
          />
        )}
        <Box
          component="h1"
          sx={{
            margin: 0,
            fontFamily: "var(--app-font-family, figtree, sans-serif)",
            fontSize: "var(--global-heading-xl-semi-bold-font-size)",
            fontWeight: "var(--global-heading-xl-semi-bold-font-weight)",
            lineHeight:
              "calc(var(--global-heading-xl-semi-bold-line-height) * 1px)",
            color: "var(--color-text-text-white)",
          }}
        >
          {agentExists
            ? `Talk to ${agent?.name || "Agent"}`
            : "Agent Unavailable"}
        </Box>
        {!agentExists && (
          <Box
            sx={{
              fontFamily: "var(--app-font-family, figtree, sans-serif)",
              fontSize: "var(--global-text-m-regular-font-size)",
              lineHeight:
                "calc(var(--global-text-m-regular-line-height) * 1px)",
              color: "var(--color-text-text-white)",
              maxWidth: 400,
            }}
          >
            The agent you are looking for does not exist or the link may be
            incorrect. Please check the URL and try again.
          </Box>
        )}
        {agentExists && (
          <MainButton
            onClick={handleAllowMicrophoneAndCamera}
            hierarchy={ButtonHierarchy.Primary}
            size={ButtonSize.xl}
            text="Start Conversation"
            disabled={isLoading}
            id="call-prescreen-start-conversation"
          />
        )}
        {!branding && agentExists && (
          <Box
            sx={{
              padding: "var(--spacing-tokens-size-in-px-spacing-spacing-lg)",
              fontFamily: "var(--app-font-family, figtree, sans-serif)",
              fontSize: "var(--global-text-s-medium-font-size)",
              fontWeight: "var(--global-text-s-medium-font-weight)",
              lineHeight: "calc(var(--global-text-s-medium-line-height) * 1px)",
              color: "var(--color-text-text-white)",
            }}
          >
            By clicking on "Start Conversation" you agree to our{" "}
            <Box
              component="a"
              href={tosLink}
              target="_blank"
              rel="noreferrer"
              sx={linkStyle}
            >
              Terms of Service
            </Box>{" "}
            and{" "}
            <Box
              component="a"
              href={privacyLink}
              target="_blank"
              rel="noreferrer"
              sx={linkStyle}
            >
              Privacy Policy
            </Box>
            . <br />
            <span
              style={{ fontSize: "var(--global-text-2-xs-regular-font-size)" }}
            >
              You are speaking with an AI avatar, not a real human.
            </span>
          </Box>
        )}
      </Box>
      <VideoAndMicAccessModal open={permissionsModalOpen} />
      <SnackbarWithText
        open={errorOpen}
        text={errorMessage}
        severity={SnackbarType.error}
        handleClose={() => {
          setErrorOpen(false)
          setErrorMessage("")
        }}
      />
    </React.Fragment>
  )
}
